"use client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";

import { jobsRepository } from "@/features/jobs/api/jobs-repository";
import type { ApplicantsCsvFilter } from "@/features/jobs/api/jobs-repository";
import type { PostJobValues } from "@/features/jobs/schemas";
import type {
  AddPlannedTasksInput,
  ApplicantsQuery,
  FinishedTasksResult,
  JobSearchParams,
  MyJobsFilter,
  PlannedTasksQuery,
  ReviewTasksInput,
  ShareType,
  SubmitJobTaskPayload,
} from "@/features/jobs/types";

/** Paginated/sorted job search for `/jobs`. Keeps the previous page visible
 * while the next loads so paging/sorting doesn't flash an empty list. */
export function useJobSearch(params: JobSearchParams) {
  return useQuery({
    queryKey: queryKeys.jobs.search(params),
    queryFn: () => jobsRepository.searchJobs(params),
    placeholderData: keepPreviousData,
  });
}

export function useJobDetail(jobId: string) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(jobId),
    queryFn: () => jobsRepository.getJobDetail(jobId),
    enabled: Boolean(jobId),
  });
}

/** Apply-time verification (`GET /api/v1/job/job_apply`). Not cached long —
 * the backend rotates the job's variables per attempt, so refetch on mount. */
export function useApplyVerification(jobId: string) {
  return useQuery({
    queryKey: queryKeys.jobs.apply(jobId),
    queryFn: () => jobsRepository.getApplyVerification(jobId),
    enabled: Boolean(jobId),
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSubmitJobTask() {
  return useMutation({
    mutationFn: (payload: SubmitJobTaskPayload) =>
      jobsRepository.submitJobTask(payload),
  });
}

/** Post a new job from `/job_create`; clears cached job lists on success. */
export function usePostJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: PostJobValues) => jobsRepository.createJob(values),
    onSuccess: () => {
      // Prefix-match invalidates every cached job search/list.
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
}

/** Platform-wide success matrix + top earners for the `/jobs` stats tab. */
export function usePlatformStats() {
  return useQuery({
    queryKey: queryKeys.jobs.platformStats(),
    queryFn: () => jobsRepository.getPlatformStats(),
    staleTime: 60_000,
  });
}

export function useInvitations() {
  return useQuery({
    queryKey: queryKeys.jobs.invitations(),
    queryFn: () => jobsRepository.getInvitations(),
  });
}

/** Posted offers for `/myoffers` (demo-only — see `PostedOffer`). */
export function useMyOffers() {
  return useQuery({
    queryKey: queryKeys.jobs.offers(),
    queryFn: () => jobsRepository.getMyOffers(),
  });
}

/** Decline an invitation, refreshing the list + the navbar invitation badge. */
export function useDeclineInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      jobsRepository.declineInvitation(inviteId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.invitations() });
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.counts() });
    },
  });
}

export function useFinishedTasks(page: number) {
  return useQuery({
    queryKey: queryKeys.jobs.finished(page),
    queryFn: () => jobsRepository.getFinishedTasks(page),
    placeholderData: keepPreviousData,
  });
}

/** Delete a pending submitted task; writes the refreshed page back into cache. */
export function useDeleteFinishedTask(page: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => jobsRepository.deleteFinishedTask(taskId),
    onSuccess: (result: FinishedTasksResult) => {
      qc.setQueryData(queryKeys.jobs.finished(page), result);
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.counts() });
    },
  });
}

export function useFinishedTaskDetail(taskId: string) {
  return useQuery({
    queryKey: queryKeys.jobs.finishedTask(taskId),
    queryFn: () => jobsRepository.getFinishedTaskDetail(taskId),
    enabled: Boolean(taskId),
  });
}

/** The employer's own jobs for one status tab; keeps the prior page while paging. */
export function useMyJobs(filter: MyJobsFilter, page: number) {
  return useQuery({
    queryKey: queryKeys.jobs.mine(filter, page),
    queryFn: () => jobsRepository.getMyJobs(filter, page),
    placeholderData: keepPreviousData,
  });
}

/** Jobs shared with the current user ("Shared With Me"), paginated on its own. */
export function useSharedJobs(page: number) {
  return useQuery({
    queryKey: queryKeys.jobs.sharedWithMe(page),
    queryFn: () => jobsRepository.getSharedJobs(page),
    placeholderData: keepPreviousData,
  });
}

/** Pause/resume one of the employer's jobs, refreshing every `/myjobs` tab. */
export function useSetJobActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, active }: { jobId: string; active: boolean }) =>
      jobsRepository.setJobActive(jobId, active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
}

/** Delete one of the employer's jobs, refreshing every `/myjobs` tab. */
export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => jobsRepository.deleteJob(jobId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
    },
  });
}

/** Submitted tasks for one of the employer's jobs (`/job_applicants`), with
 * server-side search/sort/paging. */
export function useApplicants(jobId: string, query: ApplicantsQuery) {
  return useQuery({
    queryKey: queryKeys.jobs.applicants(jobId, query),
    queryFn: () => jobsRepository.getApplicants(jobId, query),
    enabled: Boolean(jobId),
    placeholderData: keepPreviousData,
  });
}

/** A single submitted task for review (`/job_applicants/details`). */
export function useApplicantTask(jobId: string, taskId: string) {
  return useQuery({
    queryKey: queryKeys.jobs.applicantTask(jobId, taskId),
    queryFn: () => jobsRepository.getApplicantTask(jobId, taskId),
    enabled: Boolean(jobId) && Boolean(taskId),
  });
}

/** Export the applicant list to a CSV file and trigger a browser download. */
export function useDownloadApplicantsCsv(jobId: string) {
  return useMutation({
    mutationFn: async (filter: ApplicantsCsvFilter) => {
      const csv = await jobsRepository.downloadApplicantsCsv(jobId, filter);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `job-${jobId}-applicants-${filter}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });
}

/** Approve/decline submitted tasks; refreshes the list + the navbar counts. */
export function useReviewTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReviewTasksInput) => jobsRepository.reviewTasks(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.notifications.counts() });
    },
  });
}

/** Collaborators a job is shared with (owner-only Sharing tab). */
export function useJobSharing(jobId: string) {
  return useQuery({
    queryKey: queryKeys.jobs.sharing(jobId),
    queryFn: () => jobsRepository.getSharing(jobId),
    enabled: Boolean(jobId),
  });
}

/** Grant a user view/edit access; refreshes the sharing list on success. */
export function useAddShare(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, type }: { username: string; type: ShareType }) =>
      jobsRepository.addShare(jobId, username, type),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.sharing(jobId) });
    },
  });
}

/** Revoke a user's view/edit access; refreshes the sharing list on success. */
export function useRemoveShare(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, type }: { userId: string; type: ShareType }) =>
      jobsRepository.removeShare(jobId, userId, type),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.sharing(jobId) });
    },
  });
}

/** A page of device buckets for an automation job (owner/edit/view visible). */
export function useBuckets(jobId: string, page: number) {
  return useQuery({
    queryKey: queryKeys.jobs.buckets(jobId, page),
    queryFn: () => jobsRepository.getBuckets(jobId, page),
    enabled: Boolean(jobId),
    placeholderData: keepPreviousData,
  });
}

/** Overwrite one bucket's data; refreshes the buckets list on success. */
export function useUpdateBucket(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      deviceId,
      data,
    }: {
      deviceId: string;
      data: Record<string, unknown>;
    }) => jobsRepository.updateBucket(jobId, deviceId, data),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...queryKeys.jobs.all, "buckets", jobId],
      });
    },
  });
}

/** Delete one device bucket; refreshes every buckets page for the job. */
export function useDeleteBucket(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      jobsRepository.deleteBucket(jobId, deviceId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...queryKeys.jobs.all, "buckets", jobId],
      });
    },
  });
}

/** Delete every bucket for a job; refreshes every buckets page for the job. */
export function useDeleteAllBuckets(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => jobsRepository.deleteAllBuckets(jobId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...queryKeys.jobs.all, "buckets", jobId],
      });
    },
  });
}

/** A page of planned tasks for an automation job (server-driven search/sort). */
export function usePlannedTasks(jobId: string, query: PlannedTasksQuery) {
  return useQuery({
    queryKey: queryKeys.jobs.plannedTasks(jobId, query),
    queryFn: () => jobsRepository.getPlannedTasks(jobId, query),
    enabled: Boolean(jobId),
    placeholderData: keepPreviousData,
  });
}

/** Queue planned tasks; refreshes every planned-tasks page for the job. */
export function useAddPlannedTasks(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<AddPlannedTasksInput, "jobId">) =>
      jobsRepository.addPlannedTasks({ jobId, ...input }),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...queryKeys.jobs.all, "planned-tasks", jobId],
      });
    },
  });
}

/** Delete one planned task; refreshes every planned-tasks page for the job. */
export function useDeletePlannedTask(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plannedTaskId: string) =>
      jobsRepository.deletePlannedTask(plannedTaskId),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [...queryKeys.jobs.all, "planned-tasks", jobId],
      });
    },
  });
}

/** Export the job's planned tasks to a CSV file and trigger a download. */
export function useDownloadPlannedTasksCsv(jobId: string) {
  return useMutation({
    mutationFn: async () => {
      const csv = await jobsRepository.downloadPlannedTasksCsv(jobId);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `job-${jobId}-planned-tasks.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });
}

/** Resubmit proof for a "not complete" task, refreshing its detail. */
export function useResubmitTask(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobProof: string) =>
      jobsRepository.resubmitTask(taskId, jobProof),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.jobs.finishedTask(taskId),
      });
    },
  });
}
